import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaMinus } from 'react-icons/fa';
import '../Finance/Finance.css'
import financeHero from '../../../assets/Services/EPC.png';
import CustomButton from '../../Button/CustomButton';

const EPCDetail = () => {
  const [activeFAQ, setActiveFAQ] = useState(null);
  const navigate = useNavigate();

  const faqData = [
    {
      q: "1. Do you work only as an advisor or also during execution?",
      a: "We stay involved during execution to support EPC coordination, monitoring, and commissioning."
    },
    {
      q: "2. Do you work directly with EPC contractors?",
      a: " Yes. We coordinate closely with EPCs, developers, and other stakeholders during execution."
    },
    {
      q: "3. At what stage should we involve Dazbog for execution support?",
      a: "Ideally before construction starts, but we can also join ongoing projects."
    },
    {
      q: "4. Which technologies do you support during execution?",
      a: "We support solar, BESS, hybrid renewable, and green hydrogen projects."
    }
  ];

  // Animation variants for sections
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  return (
    <div className="service-detail-page">
      {/* --- HERO SECTION --- */}
      <section className="detail-hero">
        <div className="container">
          <div className="detail-hero-flex">
            <motion.div
              className="detail-hero-content"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>EPC Coordination & Execution Oversight</h1>
              <p>During construction, many things need close attention — timelines, quality, safety, and coordination between teams.</p>
              <p>We support developers and investors during this critical stage. Our role is to stay involved, coordinate EPC partners, track progress, and make sure the project moves forward as planned.</p>
              <CustomButton
                variant="white"
                size="lg"
                text=" Discuss Your Project"
                onClick={() => navigate("/contact")}
              />
            </motion.div>
            <motion.div
              className="detail-hero-image"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1 }}
            >
              <img src={financeHero} alt="Finance Advisory" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- HOW WE SUPPORT SECTION --- */}
      <section className="support-info-section">
        <div className="container">
          <motion.div
            className="support-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <h2>How We Support Your Project</h2>
            <p>Execution is where planning turns into reality.</p>
            <p>At this stage, even small miscommunication can lead to delays or cost overruns. That’s why we stay closely connected with project teams during construction.</p>
            <p className="support-lead">We focus on keeping everyone aligned, identifying issues early, and ensuring the project stays on track — technically and financially.</p>
          </motion.div>

          <div className="services-list-container">
            <motion.h3
              className="list-main-title"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              Our EPC Coordination & Execution Services
            </motion.h3>

            <motion.div
              className="services-breakdown-grid"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
            >
              {/* Service Blocks with stagger reveal */}
              {[
                {
                  title: "1. EPC Coordination & Planning",
                  text1: "Clear coordination makes execution smoother.",
                  text2: "We work with EPC contractors to align schedules, scope, and responsibilities. Our aim is to make sure there are no gaps between planning and implementation.",
                  listTitle: "Our support includes:",
                  list: ["Reviewing EPC scope and deliverables", "Supporting project planning and timelines", "Coordinating between different technical teams", "Ensuring responsibilities are clearly defined"],
                  outro: "Good coordination reduces confusion and prevents delays."
                },
                {
                  title: "2. Construction & Execution Monitoring",
                  text1: "Regular monitoring helps avoid surprises.",
                  text2: "We track site progress, review milestones, and make sure quality and safety standards are followed.",
                  listTitle: "This includes:",
                  list: ["Monitoring construction progress", "Conducting quality and safety checks", "Identifying issues early", "Supporting quick resolution of site challenges"],
                  outro: "Staying proactive helps keep the project moving smoothly."
                },
                {
                  title: "3. Owner’s Engineer Support",
                  text1: "During execution, project owners often need an independent technical view.",
                  text2: "We act as the owner’s representative and provide clear technical oversight throughout the construction phase.",
                  listTitle: "Our role may include:",
                  list: ["Reviewing design updates", "Monitoring contractor performance", "Ensuring compliance with technical standards", "Providing practical engineering guidance"],
                  outro: "We focus on protecting the owner’s interests."
                },
                {
                  title: "4. Commissioning & Handover Support",
                  text1: "The final stage is just as important as the beginning.",
                  text2: "We support the project through commissioning and early operations to ensure everything functions as expected.",
                  listTitle: "Our support includes:",
                  list: ["Coordinating commissioning activities", "Reviewing documentation and test results", "Supporting smooth handover processes", "Ensuring operational readiness"],
                  outro: "A well-managed handover sets the foundation for long-term performance."
                }
              ].map((service, index) => (
                <motion.div key={index} className="service-text-block" variants={fadeInUp}>
                  <h4>{service.title}</h4>
                  <p>{service.text1}</p>
                  <p>{service.text2}</p>
                  <p>{service.listTitle}</p>
                  <ul>
                    {service.list.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <p>{service.outro}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>

          <motion.div
            className="projects-tags-box"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3>Projects We Work On</h3>
            <div className="tag-flex">
              {[
                { name: "Solar IPP", path: "/projects/solar-ipp" },
                { name: "Solar EPC Project", path: "/projects/solar-epc" },
                { name: "Solar Hybrid EPC Project", path: "/projects/solar-hybrid" },
                { name: "Solar + BESS EPC Project", path: "/projects/solar-bess" }
              ].map((tag, i) => (
                <span
                  key={i}
                  className="project-tag-link"
                  onClick={() => navigate(tag.path)}
                >
                  {tag.name}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- FAQ SECTION --- */}
      <section className="faq-detail-section">
        <div className="container">
          <div className="faq-flex-row">
            <motion.div
              className="faq-left-content"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2>EPC Execution – Common Questions</h2>
              <p>Answers to common questions about our role during project execution.</p>
            </motion.div>
            <div className="faq-right-accordion">
              {faqData.map((faq, i) => (
                <motion.div
                  key={i}
                  className="faq-detail-item"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <div className="faq-detail-q" onClick={() => setActiveFAQ(activeFAQ === i ? null : i)}>
                    <span>{faq.q}</span>
                    {activeFAQ === i ? <FaMinus className="faq-icon" /> : <FaPlus className="faq-icon" />}
                  </div>
                  <AnimatePresence>
                    {activeFAQ === i && (
                      <motion.div
                        className="faq-detail-a"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4 }}
                      >
                        <p>{faq.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EPCDetail; 